import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { AvatarSize, AvatarTypes } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { ICommunity } from '../../../types/interfaces/community';
import { IExercise } from '../../../types/interfaces/exercise';
import { IUser } from '../../../types/interfaces/user';
import AppCommunityAvatar from '../../designe/app-entity-avatar/app-community-avatar/AppCommunityAvatar';
import AppExerciseAvatar from '../../designe/app-entity-avatar/app-exercise-avatar/AppExerciseAvatar';
import AppUserAvatar from '../../designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import Search from '../../reusable/search/Search';
import { useStyles } from './GlobalSearch.styles';
import { useGlobalSearch } from './useGlobalSearch';

const GlobalSearch = () => {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { navigate } = useNavigationStack();
  const { t } = useTranslation();
  const {
    data,
    filterValue,
    searchRef,
    isShowSearchResult,
    onClickAway,
    onSearchClick,
    onFilterValue,
    onItemSelected,
  } = useGlobalSearch();

  function getItems(
    items: IUser[] | ICommunity[] | IExercise[],
    title: string,
    itemNameCallback: (item: IUser | ICommunity | IExercise) => string,
    linkCallback: (item: IUser | ICommunity | IExercise) => string,
  ) {
    return (
      <>
        <Typography
          variant="label-text-md"
          className={classes.globalSearchResultCategoryName}>
          {title}
        </Typography>
        {items.map((item) => (
          <Box
            key={item.id}
            className={classes.globalSearchResultItem}
            onClick={() => {
              navigate(linkCallback(item));
              onItemSelected();
            }}>
            {'categoryId' in item ? (
              <AppExerciseAvatar
                exercise={item as IExercise}
                size={AvatarSize.s}
                type={AvatarTypes.round}
              />
            ) : 'firstname' in item ? (
              <AppUserAvatar
                entity={item as IUser}
                size={AvatarSize.s}
                type={AvatarTypes.round}
              />
            ) : (
              <AppCommunityAvatar
                community={item as ICommunity}
                size={AvatarSize.s}
                type={AvatarTypes.round}
              />
            )}
            <Box>
              <Typography variant="text-md-sb">
                {itemNameCallback(item)}
              </Typography>
            </Box>
          </Box>
        ))}
      </>
    );
  }

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Box>
        <Search
          fullWidth
          value={filterValue}
          onChange={onFilterValue}
          className={classes.globalSearch}
          onClick={() => onSearchClick()}
          ref={searchRef}
        />
        {searchRef?.current && (
          <Popper
            open={isShowSearchResult()}
            anchorEl={searchRef?.current}
            placement="bottom-start"
            className={classes.globalSearchResult}
            style={{
              width: searchRef?.current?.['clientWidth'] || 0,
            }}>
            <Paper elevation={3} className={classes.globalSearchResultPaper}>
              {data?.users?.length > 0 &&
                getItems(
                  data.users,
                  t('globalSearch.users'),
                  (user: IUser | IExercise | ICommunity) => {
                    if ('firstname' in user)
                      return `${user.firstname} ${user.lastname || ''}`;
                  },
                  (user: IUser | IExercise | ICommunity) =>
                    Paths.users.toUser(user.id),
                )}
              {data?.communities?.length > 0 &&
                getItems(
                  data.communities,
                  t('globalSearch.communities'),
                  (community: IUser | IExercise | ICommunity) => {
                    if ('title' in community) return community.title;
                  },
                  (community: IUser | IExercise | ICommunity) =>
                    `${Paths.communities.root}/${community.id}`,
                )}
              {data?.exercises?.length > 0 &&
                getItems(
                  data.exercises,
                  t('globalSearch.exercises'),
                  (exercise: IUser | IExercise | ICommunity) => {
                    if ('title' in exercise) return exercise.title;
                  },
                  (exercise: IUser | IExercise | ICommunity) =>
                    Paths.exercises.toEditId(exercise.id),
                )}
            </Paper>
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
